// Fallback SVG marks for integrations whose brand isn't in simple-icons (Slack
// was removed for trademark; Google Workspace, GoHighLevel, Apollo, Meta Ads,
// and Microsoft 365 use different slugs / aren't exported).
//
// Each value is a self-contained <svg viewBox="0 0 24 24">…</svg> string that
// renders inside `Logo.tsx`'s chip the same way the simple-icons SVGs do. Colour
// comes from the chip's `--chipFg` CSS variable (i.e. the integration's
// iconBrandHex), so SVGs use `currentColor` for fills.

export type CustomIcon = {
  hex: string;
  svg: string;
  // When true the SVG carries its own brand colours and must NOT be recoloured
  // to the chip's currentColor. Such marks only read on a light tile, so the
  // brand variant renders them on white instead of the brand-colour tile.
  multicolor?: boolean;
};

// The full-colour Slack mark (the rainbow "hash"). Slack was pulled from
// simple-icons for trademark, and the brand's only on-brand colour treatment is
// this four-colour version on a light background - a flat white-on-aubergine
// glyph reads as the wrong (legacy) logo. viewBox kept at Slack's native 127.
const SLACK: CustomIcon = {
  hex: "4A154B",
  multicolor: true,
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 127 127"><path d="M27.2 80c0 7.3-5.9 13.2-13.2 13.2C6.7 93.2.8 87.3.8 80c0-7.3 5.9-13.2 13.2-13.2h13.2V80z" fill="#E01E5A"/><path d="M33.8 80c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2v33c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V80z" fill="#E01E5A"/><path d="M47 27c-7.3 0-13.2-5.9-13.2-13.2C33.8 6.5 39.7.6 47 .6c7.3 0 13.2 5.9 13.2 13.2V27H47z" fill="#36C5F0"/><path d="M47 33.7c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H13.9C6.6 60.1.7 54.2.7 46.9c0-7.3 5.9-13.2 13.2-13.2H47z" fill="#36C5F0"/><path d="M99.9 46.9c0-7.3 5.9-13.2 13.2-13.2 7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H99.9V46.9z" fill="#2EB67D"/><path d="M93.2 46.9c0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V13.8C66.8 6.5 72.7.6 80 .6c7.3 0 13.2 5.9 13.2 13.2v33.1z" fill="#2EB67D"/><path d="M80 99.8c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2-7.3 0-13.2-5.9-13.2-13.2V99.8H80z" fill="#ECB22E"/><path d="M80 93.2c-7.3 0-13.2-5.9-13.2-13.2 0-7.3 5.9-13.2 13.2-13.2h33.1c7.3 0 13.2 5.9 13.2 13.2 0 7.3-5.9 13.2-13.2 13.2H80z" fill="#ECB22E"/></svg>`,
};

const GOOGLE_WORKSPACE: CustomIcon = {
  hex: "4285F4",
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 3l-9 4.5v9L12 21l9-4.5v-9L12 3zm0 2.236l6.764 3.382L12 12 5.236 8.618 12 5.236zM4.5 9.864L11.25 13.24v6.282L4.5 16.137V9.864zm15 0v6.273l-6.75 3.385V13.24L19.5 9.864z"/></svg>`,
};

const MICROSOFT_365: CustomIcon = {
  hex: "D83B01",
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M3 5.83L13.5 3v17.39L3 18.17V5.83zm12 .04L21 4.5v14.97l-6 1.37V5.87zM4.5 7.17v9.66l7.5 1.6V5.57l-7.5 1.6z"/></svg>`,
};

const GO_HIGH_LEVEL: CustomIcon = {
  hex: "1F4FFF",
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.236L19.236 8 12 11.764 4.764 8 12 4.236zM4 9.618l7 3.5v7.882l-7-3.5V9.618zm9 11.382v-7.882l7-3.5v7.882l-7 3.5z"/></svg>`,
};

const APOLLO: CustomIcon = {
  hex: "0B57D0",
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm0-14a6 6 0 1 0 6 6 6 6 0 0 0-6-6zm0 10a4 4 0 1 1 4-4 4 4 0 0 1-4 4z"/></svg>`,
};

// Microsoft Teams isn't in simple-icons (trademark) and the dashboard slug is
// `microsoft-teams`. A rounded square with a cut-out "T" reads as the Teams mark.
const MICROSOFT_TEAMS: CustomIcon = {
  hex: "6264A7",
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M5 4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H5Zm2.4 4.2v1.8h3.05v6.8h1.9v-6.8h3.05V8.2H7.4Z"/></svg>`,
};

const META_ADS: CustomIcon = {
  hex: "1877F2",
  svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.477 2 2 6.477 2 12c0 4.99 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.128 22 16.991 22 12c0-5.523-4.477-10-10-10z"/></svg>`,
};

export const CUSTOM_ICONS: Record<string, CustomIcon> = {
  slack: SLACK,
  googleworkspace: GOOGLE_WORKSPACE,
  // Note: dashboard uses slug `google` for Google Workspace; treat both.
  google: GOOGLE_WORKSPACE,
  microsoft365: MICROSOFT_365,
  "microsoft-teams": MICROSOFT_TEAMS,
  gohighlevel: GO_HIGH_LEVEL,
  apollo: APOLLO,
  metaads: META_ADS,
};
